import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import LinearProgress from '@mui/material/LinearProgress';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import { SectionCard, StatusChip, getModuleIcon } from '@platform/ui-kit';
import { UNLIMITED_SEATS, isUnlimitedSeats } from '@platform/entitlements';
import { api, type EntitlementRow } from '../../api';
import { describeError, useAsyncData } from './useAsyncData';

/**
 * Entitlements & licensing (spec §B).
 *
 * A table, not a grid of cards: nine rows of {toggle, number, usage} is tabular data,
 * and cards gave each row a card's visual weight for no gain.
 *
 * Seat semantics, per the owner:
 *   0    — no seats, the application is NOT available even when switched on
 *   9999 — unlimited
 */
export function EntitlementsTab() {
  const { data, loading, error, reload } = useAsyncData(() => api.admin.getEntitlements());
  const [busyModule, setBusyModule] = useState<string>();
  const [actionError, setActionError] = useState<string>();

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">{error}</Alert>;

  const rows = data?.entitlements ?? [];

  const update = async (
    moduleKey: string,
    patch: { enabled?: boolean; seatLimit?: number; temporarySeatLimit?: number },
  ) => {
    setBusyModule(moduleKey);
    setActionError(undefined);
    try {
      await api.admin.updateEntitlement(moduleKey, patch);
      reload();
    } catch (err) {
      setActionError(describeError(err));
    } finally {
      setBusyModule(undefined);
    }
  };

  return (
    <Stack spacing={2}>
      {actionError ? (
        <Alert severity="error" onClose={() => setActionError(undefined)}>
          {actionError}
        </Alert>
      ) : null}

      {/*
        ONE KIND OF LICENCE. The owner's ruling (2026-08-07): there is no permanent /
        temporary distinction anywhere. The temporary pool is gone from this screen —
        its columns, its input and its explanation.
      */}
      <Alert severity="info">
        <strong>Seats</strong> are licences. <strong>0</strong> means none available and{' '}
        <strong>{UNLIMITED_SEATS}</strong> means unlimited.
      </Alert>

      <SectionCard title="Licensed modules" disableContentPadding>
        <Box sx={{ overflowX: 'auto' }}>
          <Box
            component="table"
            sx={{
              width: '100%',
              borderCollapse: 'collapse',
              '& th': {
                textAlign: 'left',
                fontSize: 12,
                fontWeight: 600,
                letterSpacing: '0.02em',
                color: 'text.secondary',
                px: 2.5,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                whiteSpace: 'nowrap',
              },
              '& td': {
                px: 2.5,
                py: 1.5,
                borderBottom: 1,
                borderColor: 'divider',
                verticalAlign: 'middle',
              },
            }}
          >
            <thead>
              <tr>
                <th style={{ width: 72 }}>Licensed</th>
                <th>Module</th>
                <th style={{ width: 110 }}>Seats</th>
                <th style={{ width: 210 }}>Seat usage</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <EntitlementRowView
                  key={row.module_key}
                  row={row}
                  busy={busyModule === row.module_key}
                  onChange={(patch) => update(row.module_key, patch)}
                />
              ))}
            </tbody>
          </Box>
        </Box>
      </SectionCard>
    </Stack>
  );
}

function EntitlementRowView({
  row,
  busy,
  onChange,
}: {
  row: EntitlementRow;
  busy: boolean;
  onChange: (patch: { enabled?: boolean; seatLimit?: number; temporarySeatLimit?: number }) => void;
}) {
  const [seatDraft, setSeatDraft] = useState(String(row.seat_limit));
  const Icon = getModuleIcon(row.icon ?? '');
  const unlimited = isUnlimitedSeats(row.seat_limit);

  const commit = (
    draft: string,
    current: number,
    reset: (value: string) => void,
    apply: (value: number) => void,
  ) => {
    const next = Number(draft.trim());
    if (!Number.isInteger(next) || next < 0 || next > UNLIMITED_SEATS) {
      reset(String(current));
      return;
    }
    if (next !== current) apply(next);
  };

  return (
    <tr>
      <td>
        <Tooltip title={row.enabled ? 'Licensed' : 'Not licensed'}>
          <Switch
            checked={row.enabled}
            disabled={busy}
            /*
             * The toggle carries the seats with it, in one save.
             *
             * Un-licensing left the seat count standing, so a module could read
             * "not licensed — 25 seats", which is not a state that means anything.
             * Licensing left it at 0, and 0 seats means unavailable even when switched
             * on, so switching a module ON appeared to do nothing at all.
             *
             *   OFF -> 0 seats, always.
             *   ON  -> 10 seats, but ONLY if it currently has none. A module already
             *          configured with 25 is left at 25 — 10 is a starting default,
             *          not a reset, and silently discarding a real seat count would be
             *          the worse failure of the two.
             */
            onChange={(e) => {
              const enabled = e.target.checked;
              if (!enabled) return onChange({ enabled: false, seatLimit: 0 });
              return onChange(
                row.seat_limit > 0 ? { enabled: true } : { enabled: true, seatLimit: 10 },
              );
            }}
            inputProps={{ 'aria-label': `Licence ${row.name}` }}
          />
        </Tooltip>
      </td>

      <td>
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Box
            aria-hidden
            sx={{
              display: 'grid',
              placeItems: 'center',
              width: 32,
              height: 32,
              borderRadius: 1,
              bgcolor: 'action.selected',
              color: row.available ? 'primary.main' : 'text.disabled',
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small" />
          </Box>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" fontWeight={500}>
              {row.name}
            </Typography>
            {!row.in_catalog ? <StatusChip tone="warning" label="Not in catalogue" /> : null}
          </Box>
        </Stack>
      </td>

      <td>
                  <TextField
            size="small"
            value={seatDraft}
            disabled={busy}
            onChange={(e) => setSeatDraft(e.target.value)}
            onBlur={() =>
              commit(seatDraft, row.seat_limit, setSeatDraft, (v) => onChange({ seatLimit: v }))
            }
            onKeyDown={(e) => {
              if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
            }}
            inputProps={{ inputMode: 'numeric', 'aria-label': `Seats for ${row.name}` }}
            sx={{ width: 90 }}
          />
      </td>

      <td>
        <SeatUsage
          limit={row.seat_limit}
          used={row.seats_used}
          available={row.seats_available}
          unlimited={unlimited}
          emptyLabel="No seats"
        />
      </td>

    </tr>
  );
}

/** The seat-usage cell. */
function SeatUsage({
  limit,
  used,
  available,
  unlimited,
  emptyLabel,
}: {
  limit: number;
  used: number;
  available: number | null;
  unlimited: boolean;
  emptyLabel: string;
}) {
  if (unlimited) {
    return (
      <Typography variant="body2" color="text.secondary">
        {used} in use · unlimited
      </Typography>
    );
  }
  if (limit === 0) {
    return (
      <Typography variant="body2" color="text.disabled">
        {emptyLabel}
      </Typography>
    );
  }

  const percent = Math.min(100, (used / limit) * 100);
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">
        {used} of {limit} used · {available} free
      </Typography>
      <LinearProgress
        variant="determinate"
        value={percent}
        color={percent >= 100 ? 'error' : percent >= 80 ? 'warning' : 'primary'}
        sx={{ mt: 0.5, height: 6, borderRadius: 3 }}
      />
    </Box>
  );
}
